import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { labApi } from '@/api/lab';
import { LabTestStatus } from '@/types/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import ViewTestResult from '@/pages/lab/ViewTestResult';
import { FlaskConical, Eye, RefreshCw, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
    [LabTestStatus.PENDING]: { label: 'Awaiting billing', className: 'bg-amber-100 text-amber-800' },
    [LabTestStatus.PROCESSING]: { label: 'In lab', className: 'bg-blue-100 text-blue-800' },
    [LabTestStatus.COMPLETED]: { label: 'Report ready', className: 'bg-green-100 text-green-800' },
    [LabTestStatus.SENT_EXTERNAL]: { label: 'External lab', className: 'bg-purple-100 text-purple-800' },
};

interface ConsultationLabReportsProps {
    appointmentId: string;
}

export default function ConsultationLabReports({ appointmentId }: ConsultationLabReportsProps) {
    const [viewingTestId, setViewingTestId] = useState<string | null>(null);
    const [viewingTestName, setViewingTestName] = useState<string>('');

    const { data: labTests = [], isLoading, refetch, isFetching } = useQuery({
        queryKey: ['appointment-lab-tests', appointmentId],
        queryFn: async () => {
            const response = await labApi.getOrderedTestsByAppointment(appointmentId);
            return response.data?.data || [];
        },
        enabled: !!appointmentId,
        refetchInterval: 30_000,
    });

    const completedCount = labTests.filter(
        (t: { status: string }) => t.status === LabTestStatus.COMPLETED
    ).length;

    return (
        <>
            <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
                <div className="border-b border-slate-100 bg-gradient-to-r from-indigo-50/80 to-white px-5 py-4 sm:px-6">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
                                <FlaskConical className="h-4 w-4" />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-slate-900">Lab tests & reports</h2>
                                <p className="text-sm text-slate-500">
                                    {labTests.length > 0
                                        ? `${completedCount} of ${labTests.length} report${labTests.length !== 1 ? 's' : ''} ready`
                                        : 'Track billing, lab processing, and reports here'}
                                </p>
                            </div>
                        </div>
                        {labTests.length > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="gap-1.5"
                            >
                                <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
                                Refresh
                            </Button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex items-center gap-2 px-5 py-8 text-sm text-slate-500">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading lab tests…
                    </div>
                ) : labTests.length === 0 ? (
                    <div className="px-5 py-6 sm:px-6">
                        <div className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 p-5">
                            <div className="mb-3 flex items-center gap-2 text-indigo-900">
                                <ClipboardList className="h-5 w-5" />
                                <span className="text-sm font-semibold">How lab-first consultation works</span>
                            </div>
                            <ol className="space-y-2 text-sm text-slate-600">
                                <li className="flex gap-2">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">1</span>
                                    Add lab tests in the prescription panel, then click <strong>Send lab tests for billing</strong> in the summary.
                                </li>
                                <li className="flex gap-2">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">2</span>
                                    Reception adds tests to the OPD bill and collects payment — status shows <em>Awaiting billing</em> until then.
                                </li>
                                <li className="flex gap-2">
                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">3</span>
                                    Lab technician runs tests; reports appear here when ready. Review them, then complete diagnosis and prescription.
                                </li>
                            </ol>
                        </div>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 p-4 sm:p-5">
                        {labTests.map((test: { id: string; status: string; labTest: { name: string } }) => {
                            const statusInfo = STATUS_LABELS[test.status] ?? {
                                label: test.status,
                                className: 'bg-slate-100 text-slate-700',
                            };
                            const canView =
                                test.status === LabTestStatus.COMPLETED ||
                                test.status === LabTestStatus.SENT_EXTERNAL;

                            return (
                                <div
                                    key={test.id}
                                    className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-slate-900">{test.labTest.name}</p>
                                        <Badge className={cn('mt-1 text-xs font-normal', statusInfo.className)}>
                                            {statusInfo.label}
                                        </Badge>
                                    </div>
                                    {canView ? (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="gap-1.5 border-green-300 text-green-800 hover:bg-green-50"
                                            onClick={() => {
                                                setViewingTestId(test.id);
                                                setViewingTestName(test.labTest.name);
                                            }}
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                            View report
                                        </Button>
                                    ) : (
                                        <span className="text-xs text-slate-400">
                                            {test.status === LabTestStatus.PENDING
                                                ? 'Waiting at reception / billing'
                                                : 'Processing in lab'}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <Dialog
                open={!!viewingTestId}
                onOpenChange={(open) => {
                    if (!open) {
                        setViewingTestId(null);
                        setViewingTestName('');
                    }
                }}
            >
                <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{viewingTestName || 'Lab report'}</DialogTitle>
                    </DialogHeader>
                    {viewingTestId && (
                        <ViewTestResult
                            appointmentLabTestId={viewingTestId}
                            testName={viewingTestName}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
